import React, { FC, useEffect, useState } from 'react';
import { ReactComponent as CalendarIcon } from '../icons/calendar.svg';
import { ReactComponent as ClockIcon } from '../icons/clock-red.svg';
import { ReactComponent as RepeatIcon } from '../icons/repeat.svg';
import { ReactComponent as RightArrow } from '../icons/chevron-right.svg';
import { BaseModal, Button, Input } from '@cokitchen/cokitchen-components';
import { InstantTabPayload, ScheduleSurgePayload, ScheduleTabPayload } from '../types';
import { ScheduleSurgeType } from 'models/surge.model';
import DatePicker, { SelectionRangeType } from 'components/base/calendar';
import { TimeSelectorType } from 'components/base/time-selector/types';
import { formatSurgeDate } from 'utils/functions';
import { ENUM_AM_PM } from 'utils/enum';
import TimeSelector from 'components/base/time-selector';

type ScheduleSectionType = {
  value: ScheduleTabPayload;
  setValue: (arg1: ScheduleTabPayload) => void;
  surgeItem?: ScheduleSurgeType;
};

const repeatListItems = ['daily', 'weekly', 'monthly', 'quarterly', 'yearly'];

const SurgeScheduleSection: FC<ScheduleSectionType> = ({ setValue, surgeItem }) => {
  const [surge, setSurge] = useState<InstantTabPayload>({
    logistics_surge: '',
    logistics_surge_type: ''
  });
  const [selectionRange, setSelectionRange] = useState<SelectionRangeType>({
    startDate: new Date(),
    endDate: new Date(),
    key: 'selection'
  } as SelectionRangeType);
  const [startTime, setStartTime] = useState({} as TimeSelectorType);
  const [endTime, setEndTime] = useState({} as TimeSelectorType);
  const [selectedRepeat, setSelectedRepeat] = useState('');
  const [openedOption, setOpenedOption] = useState('');
  const [hasEditedTime, setHasEditedTime] = useState(false);
  const [hasEditedDate, setHasEditedDate] = useState(false);
  const [tempSelectedValues, setTempSelectedValues] = useState({
    startTime,
    endTime,
    date: { ...selectionRange },
    repeat: selectedRepeat
  });

  useEffect(() => {
    if (startTime.minutes || endTime.minutes) setHasEditedTime(true);
  }, [startTime, endTime]);

  useEffect(() => {
    const startDate = selectionRange.startDate;
    const endDate = selectionRange.endDate;
    if (selectedRepeat !== '' && startDate !== endDate) {
      setSelectionRange({
        startDate,
        endDate: startDate,
        key: 'selection'
      });
    }
  }, [selectedRepeat]);

  useEffect(() => {
    const startDate = selectionRange?.startDate?.toISOString().split('T')[0];
    const endDate = selectionRange?.endDate?.toISOString().split('T')[0];
    if (startDate !== endDate) setSelectedRepeat('');
  }, [selectionRange]);

  // TO   EDIT SURGE
  const singleSurgeData = () => {
    const surgeDate = formatSurgeDate(surgeItem?.surge_task?.surge_task_date ?? '');
    const startTimeValue = surgeItem?.surge_task.start_time.split(':') ?? '';
    const startTimeHour = Number(startTimeValue[0]);
    const startTimeHourFormatted = Number(startTimeValue[0]) - 12;
    const startTimeMinutes = startTimeValue[1];
    const startTime: TimeSelectorType = {
      hours: String(startTimeHourFormatted),
      minutes: startTimeMinutes,
      amPm: startTimeHour > 12 ? ENUM_AM_PM.PM : ENUM_AM_PM.AM
    };
    const endTimeValue = surgeItem?.surge_task.end_time.split(':') ?? '';
    const endTimeHour = Number(endTimeValue[0]);
    const endTimeHourFormatted = Number(endTimeValue[0]) - 12;
    const endTimeMinutes = endTimeValue[1];
    const endTime: TimeSelectorType = {
      hours: String(endTimeHourFormatted),
      minutes: endTimeMinutes,
      amPm: endTimeHour > 12 ? ENUM_AM_PM.PM : ENUM_AM_PM.AM
    };
    setSelectionRange({
      startDate: surgeDate?.startDate,
      endDate: surgeDate?.endDate,
      key: 'selection'
    });
    setStartTime(startTime);
    setEndTime(endTime);
    setSelectedRepeat(surgeItem?.surge_task?.repeat_time_frames[0] ?? '');
    setSurge({
      logistics_surge: surgeItem?.logistics_surge ?? '',
      logistics_surge_type: surgeItem?.logistics_surge_type ?? ''
    });
  };
  useEffect(() => {
    if (surgeItem) singleSurgeData();
  }, [surgeItem]);
  useEffect(() => {
    const startDate = selectionRange?.startDate?.toISOString().split('T')[0];
    const endDate = selectionRange?.endDate?.toISOString().split('T')[0];
    const surge_task_date = startDate === endDate ? startDate : `${startDate}-${endDate}`;
    const repeat = selectedRepeat !== '';

    const updatedValue: ScheduleSurgePayload = {
      end_time: endTime.timeWithColon ?? '',
      start_time: startTime.timeWithColon ?? '',
      logistics_surge: surge.logistics_surge,
      logistics_surge_type: surge.logistics_surge_type,
      repeat,
      repeat_time_frames: repeat ? [selectedRepeat] : [],
      surge_task_date
    };
    if (surgeItem) updatedValue.surge_task_id = surgeItem?.surge_task_id;
    setValue(updatedValue);
  }, [startTime, endTime, selectedRepeat, selectionRange, surge]);

  const formatTimeToAmPm = (time: TimeSelectorType) => {
    const { hours, minutes, amPm } = time;
    return `${hours}:${minutes} ${amPm?.toLowerCase()}`;
  };

  const getTimeValue = () => {
    if (!hasEditedTime) return 'Select time';
    return `${formatTimeToAmPm(startTime)} - ${formatTimeToAmPm(endTime)}`;
  };

  const getDateValue = () => {
    if (!hasEditedDate && !surgeItem) return 'Select date';
    const startDate = selectionRange.startDate.toDateString();
    const endDate = selectionRange.endDate.toDateString();
    return startDate === endDate ? startDate : `${startDate} - ${endDate}`;
  };

  const getRepeatValue = () => {
    return selectedRepeat ? `Repeat ${selectedRepeat}` : 'Do not repeat';
  };

  const scheduleOptions = [
    {
      name: getDateValue(),
      option: 'datepicker',
      icon: <CalendarIcon />,
      title: 'Select date'
    },
    {
      name: getTimeValue(),
      option: 'timepicker',
      icon: <ClockIcon />,
      title: 'Select time'
    },
    {
      name: getRepeatValue(),
      option: 'repeat-list',
      icon: <RepeatIcon />,
      title: 'Select repeat pattern'
    }
  ];

  const handleRepeatChange = (repeat: string) => {
    setTempSelectedValues({ ...tempSelectedValues, repeat });
  };

  const handleDateChange = (ranges: any) => {
    setHasEditedDate(true);
    setTempSelectedValues({ ...tempSelectedValues, date: ranges.selection });
  };

  const handleTimeChange = (key: string, value: TimeSelectorType) => {
    setTempSelectedValues({ ...tempSelectedValues, [key]: value });
  };

  const toggleOpenedOption = (option: string) => {
    const newOption = option === openedOption ? '' : option;
    setOpenedOption(newOption);
    setTempSelectedValues({
      startTime,
      endTime,
      date: { ...selectionRange },
      repeat: selectedRepeat
    });
  };

  const updateSelectedValues = () => {
    const { startTime: start, endTime: end, date, repeat } = tempSelectedValues;
    setStartTime(start);
    setEndTime(end);
    setSelectionRange(date);
    setSelectedRepeat(repeat);
    setOpenedOption('');
  };

  return (
    <section className='-mx-[1.75rem] mt-6 relative'>
      {scheduleOptions.map(({ icon, name, option }) => (
        <div
          key={option}
          role='button'
          onClick={() => toggleOpenedOption(option)}
          className='border-l-4 border-transparent border-t border-t-[#ebebeb] flex py-4 px-6 items-center cursor-pointer transition-all hover:border-l-red-5 hover:bg-[#F4F5F5]'>
          {icon}
          <p className='grow pl-4'>{name}</p>
          <RightArrow />
        </div>
      ))}
      <BaseModal
        isOpen={!!openedOption}
        title={scheduleOptions.find((o) => o.option === openedOption)?.title ?? 'Select'}
        titleIcon={<></>}
        titleIconColor='info'
        onRequestClose={() => toggleOpenedOption('')}
        footer={
          <Button variant='block' color='primary' small onClick={updateSelectedValues}>
            Select
          </Button>
        }>
        {openedOption === 'datepicker' && (
          <DatePicker handleSelect={handleDateChange} selectionRange={tempSelectedValues.date} />
        )}
        {openedOption === 'timepicker' && (
          <div className='flex max-md:flex-col gap-4 items-center justify-evenly rounded p-3 pb-4'>
            <div>
              <p className='text-gray-5 text-xs leading-[19px] mb-1'>From:</p>
              <TimeSelector
                value={tempSelectedValues.startTime}
                onChange={(v) => handleTimeChange('startTime', v)}
              />
            </div>
            <div>
              <p className='text-gray-5 text-xs leading-[19px] mb-1'>To:</p>
              <TimeSelector
                value={tempSelectedValues.endTime}
                onChange={(v) => handleTimeChange('endTime', v)}
              />
            </div>
          </div>
        )}
        {openedOption === 'repeat-list' && (
          <ul className='min-w-[240px] p-5'>
            <li className='mb-4'>
              <Input
                type='radio'
                label='Do not repeat'
                checked={tempSelectedValues.repeat === ''}
                className='text-sm'
                name='repeat'
                onChange={() => handleRepeatChange('')}
              />
            </li>
            {repeatListItems.map((item) => (
              <li key={item} className='mb-4 last:mb-0'>
                <Input
                  type='radio'
                  label={`Repeat ${item}`}
                  checked={tempSelectedValues.repeat === item}
                  className='text-sm'
                  name='repeat'
                  onChange={() => handleRepeatChange(item)}
                />
              </li>
            ))}
          </ul>
        )}
      </BaseModal>
    </section>
  );
};

export default SurgeScheduleSection;
